import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, money, formatDate } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, Users, CalendarCheck, Wallet, TrendingUp, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const statusStyle = {
  available: "bg-[#EAF4F0] text-[#2D6A4F] border-[#cfe6db]",
  occupied: "bg-gold-light text-terracotta border-[#f0dcc4]",
  maintenance: "bg-[#F9EAEB] text-destructive border-[#f0d2d3]",
};

export default function Dashboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const canEdit = user?.role === "admin";
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: () => api.get("/dashboard/stats").then((r) => r.data) });
  const { data: units = [] } = useQuery({ queryKey: ["units"], queryFn: () => api.get("/units").then((r) => r.data) });
  const { data: reservations = [] } = useQuery({ queryKey: ["reservations"], queryFn: () => api.get("/reservations").then((r) => r.data) });
  const { data: tenants = [] } = useQuery({ queryKey: ["tenants"], queryFn: () => api.get("/tenants").then((r) => r.data) });
  const [editUnit, setEditUnit] = useState(null);

  const resFor = (unitId) => reservations.find((r) => r.unit_id === unitId && r.status === "active");
  const tenantFor = (unitId) => {
    const res = resFor(unitId);
    if (!res) return null;
    return tenants.find((tt) => tt.id === res.tenant_id);
  };

  const saveUnit = async () => {
    await api.put(`/units/${editUnit.id}`, {
      name: editUnit.name, type: editUnit.type, floor: editUnit.floor,
      base_rent: Number(editUnit.base_rent), status: editUnit.status,
      price_weekly: Number(editUnit.price_weekly || 0), price_daily: Number(editUnit.price_daily || 0),
    });
    qc.invalidateQueries({ queryKey: ["units"] });
    qc.invalidateQueries({ queryKey: ["stats"] });
    setEditUnit(null);
    toast.success(t("common.save"));
  };

  const cards = [
    { key: "occupancy", value: `${stats?.occupancy_rate ?? 0}%`, icon: TrendingUp, accent: "text-teak" },
    { key: "activeRes", value: stats?.active_reservations ?? 0, icon: CalendarCheck, accent: "text-terracotta" },
    { key: "tenants", value: stats?.tenants ?? 0, icon: Users, accent: "text-teak" },
    { key: "monthIncome", value: money(stats?.month_income), icon: Wallet, accent: "text-terracotta" },
    { key: "depositsHeld", value: money(stats?.deposits_held), icon: ShieldCheck, accent: "text-teak" },
  ];

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground mb-1">{t("appName")}</p>
        <h1 className="text-3xl sm:text-4xl font-light tracking-tight text-teak">{t("dash.title")}</h1>
        <p className="text-muted-foreground mt-1">{t("dash.subtitle")}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {cards.map((c) => (
          <Card key={c.key} className="p-4 border-border shadow-[0_4px_20px_rgba(42,42,42,0.04)]" data-testid={`stat-${c.key}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-[0.08em] text-muted-foreground">{t(`dash.${c.key}`)}</span>
              <c.icon className={`w-4 h-4 ${c.accent}`} />
            </div>
            <div className="text-2xl font-semibold text-foreground mt-3">{c.value}</div>
          </Card>
        ))}
      </div>

      <div>
        <div className="flex items-center gap-2 mb-4">
          <Building2 className="w-5 h-5 text-teak" />
          <h2 className="text-xl font-semibold text-teak">{t("dash.units")}</h2>
          <div className="flex gap-3 ml-auto text-xs">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#2D6A4F]" />{t("unit.available")} {stats?.available ?? 0}</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-terracotta" />{t("unit.occupied")} {stats?.occupied ?? 0}</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-destructive" />{t("unit.maintenance")} {stats?.maintenance ?? 0}</span>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {units.map((u) => {
            const tn = tenantFor(u.id);
            return (
              <button key={u.id} onClick={() => setEditUnit({ ...u })} data-testid={`unit-card-${u.number}`}
                className="text-left group">
                <Card className={`p-4 border transition-transform duration-200 group-hover:-translate-y-1 group-hover:shadow-[0_8px_28px_rgba(42,42,42,0.08)] ${statusStyle[u.status]}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-xs uppercase tracking-[0.1em] opacity-70">{t(`unit.type_${u.type}`)}</div>
                      <div className="text-2xl font-semibold">{u.number}</div>
                    </div>
                    <span className="text-[10px] uppercase tracking-wide font-medium px-2 py-1 rounded-full bg-white/60">
                      {t(`unit.${u.status}`)}
                    </span>
                  </div>
                  <div className="mt-3 text-sm min-h-[20px] truncate">
                    {tn ? `${tn.first_name} ${tn.last_name}` : <span className="opacity-50">—</span>}
                  </div>
                  <div className="text-xs opacity-70 mt-1">{money(u.base_rent)}</div>
                </Card>
              </button>
            );
          })}
        </div>
      </div>

      <Dialog open={!!editUnit} onOpenChange={(o) => !o && setEditUnit(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{(canEdit ? t("unit.editUnit") : t("dash.unitInfo"))} · {editUnit?.number}</DialogTitle></DialogHeader>
          {editUnit && (
            <div className="space-y-4">
              {(() => {
                const res = resFor(editUnit.id);
                const tn = res ? tenants.find((x) => x.id === res.tenant_id) : null;
                return (
                  <div className="rounded-md border border-border bg-sand/40 p-3" data-testid="unit-occupant">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">{t("dash.occupant")}</div>
                    {tn ? (
                      <div className="space-y-0.5">
                        <div className="font-semibold text-teak" data-testid="occupant-name">{tn.first_name} {tn.last_name}</div>
                        <div className="text-sm text-muted-foreground">{t(`res.${res.rental_type}`)} · {formatDate(res.start_date)} → {res.end_date ? formatDate(res.end_date) : "…"}</div>
                        <div className="text-sm">{t("res.rent")}: {money(res.rent_amount)} · {t("res.deposit")}: {money(res.deposit_amount)}</div>
                        <button type="button" onClick={() => navigate("/admin/tenants")} className="text-sm text-terracotta hover:underline mt-1" data-testid="occupant-view-tenant">{t("dash.viewTenant")}</button>
                      </div>
                    ) : <div className="text-sm text-muted-foreground" data-testid="occupant-vacant">{t("dash.vacant")}</div>}
                  </div>
                );
              })()}
              {canEdit && (<>
              <div className="space-y-2"><Label>{t("unit.name")}</Label>
                <Input value={editUnit.name} onChange={(e) => setEditUnit({ ...editUnit, name: e.target.value })} data-testid="unit-name-input" /></div>
              <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>{t("unit.type")}</Label>
                  <Select value={editUnit.type} onValueChange={(v) => setEditUnit({ ...editUnit, type: v })}>
                    <SelectTrigger data-testid="unit-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="studio">{t("unit.type_studio")}</SelectItem>
                      <SelectItem value="apartment_2">{t("unit.type_apartment_2")}</SelectItem>
                      <SelectItem value="conciergerie">{t("unit.type_conciergerie")}</SelectItem>
                    </SelectContent>
                  </Select></div>
                <div className="space-y-2"><Label>{t("unit.floor")}</Label>
                  <Select value={editUnit.floor} onValueChange={(v) => setEditUnit({ ...editUnit, floor: v })}>
                    <SelectTrigger data-testid="unit-floor-select"><SelectValue placeholder={t("unit.floor")} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ground">{t("unit.floor_ground")}</SelectItem>
                      <SelectItem value="first">{t("unit.floor_first")}</SelectItem>
                    </SelectContent>
                  </Select></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2"><Label className="text-xs">{t("unit.rentMonth")}</Label>
                  <Input type="number" value={editUnit.base_rent} onChange={(e) => setEditUnit({ ...editUnit, base_rent: e.target.value })} data-testid="unit-rent-input" /></div>
                <div className="space-y-2"><Label className="text-xs">{t("unit.rentWeek")}</Label>
                  <Input type="number" value={editUnit.price_weekly ?? 0} onChange={(e) => setEditUnit({ ...editUnit, price_weekly: e.target.value })} data-testid="unit-week-input" /></div>
                <div className="space-y-2"><Label className="text-xs">{t("unit.rentDay")}</Label>
                  <Input type="number" value={editUnit.price_daily ?? 0} onChange={(e) => setEditUnit({ ...editUnit, price_daily: e.target.value })} data-testid="unit-day-input" /></div>
              </div>
              <div className="space-y-2"><Label>{t("common.status")}</Label>
                <Select value={editUnit.status} onValueChange={(v) => setEditUnit({ ...editUnit, status: v })}>
                  <SelectTrigger data-testid="unit-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="available">{t("unit.available")}</SelectItem>
                    <SelectItem value="occupied">{t("unit.occupied")}</SelectItem>
                    <SelectItem value="maintenance">{t("unit.maintenance")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              </>)}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUnit(null)}>{canEdit ? t("common.cancel") : t("common.close")}</Button>
            {canEdit && <Button onClick={saveUnit} className="bg-teak hover:bg-teak-hover text-white" data-testid="unit-save-btn">{t("common.save")}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
